import { useEffect, useRef, useState } from 'react';
import { MapAnimations } from '../components/MapAnimations';
import { PixelButton } from '../components/PixelButton';

type MapMode = 'day' | 'night';
const MAP_MODE_KEY = 'cosy-map-mode';

export function MapScreen() {
  const [mode, setMode] = useState<MapMode>(
    () => (localStorage.getItem(MAP_MODE_KEY) as MapMode) ?? 'day',
  );
  const [sweepKey, setSweepKey] = useState(0);
  const firstPaint = useRef(true);

  useEffect(() => {
    document.body.classList.add('view-map');
    return () => {
      document.body.classList.remove('view-map');
      document.body.removeAttribute('data-map-mode');
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(MAP_MODE_KEY, mode);
    document.body.setAttribute('data-map-mode', mode);
    // Don't play the dusk sweep when the route first mounts — only on user toggle.
    if (firstPaint.current) {
      firstPaint.current = false;
      return;
    }
    setSweepKey(k => k + 1);
  }, [mode]);

  const toggle = () => setMode(m => (m === 'day' ? 'night' : 'day'));

  return (
    <>
      <div className="map-view__layer map-view__layer--day" aria-hidden="true" />
      <div className="map-view__layer map-view__layer--night" aria-hidden="true" />
      <MapAnimations />
      <div className="map-view__frame" aria-hidden="true" />
      {sweepKey > 0 && (
        <div
          key={sweepKey}
          className={`map-view__sweep map-view__sweep--${mode}`}
          aria-hidden="true"
        />
      )}
      <div className="map-view__toggle">
        <PixelButton variant="gold" onClick={toggle}>
          {mode === 'day' ? <Moon /> : <Sun />}
          {mode === 'day' ? 'NIGHTFALL' : 'DAYBREAK'}
        </PixelButton>
      </div>
    </>
  );
}

/** Pixel-drawn crescent — a stand-in for the Unicode moon glyph, which
 *  falls back to a non-pixel font and drifts off the baseline. */
function Moon() {
  return (
    <svg
      aria-hidden="true"
      width={12}
      height={12}
      viewBox="0 0 6 6"
      shapeRendering="crispEdges"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <rect x="1" y="0" width="3" height="1" fill="currentColor" />
      <rect x="0" y="1" width="2" height="4" fill="currentColor" />
      <rect x="1" y="5" width="3" height="1" fill="currentColor" />
    </svg>
  );
}

/** Pixel-drawn sun with four rays — matches the Moon icon's metrics so
 *  the toggle button's width stays roughly steady across states. */
function Sun() {
  return (
    <svg
      aria-hidden="true"
      width={12}
      height={12}
      viewBox="0 0 6 6"
      shapeRendering="crispEdges"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <rect x="2" y="2" width="2" height="2" fill="currentColor" />
      <rect x="2" y="0" width="2" height="1" fill="currentColor" />
      <rect x="2" y="5" width="2" height="1" fill="currentColor" />
      <rect x="0" y="2" width="1" height="2" fill="currentColor" />
      <rect x="5" y="2" width="1" height="2" fill="currentColor" />
    </svg>
  );
}
